<template>
  <CPreloader v-if="!product" />

  <WrapperWithCard
    v-if="product"
    :enable-card="!isMobile"
    card-classname="product-view"
  >
    <div class="product-view__header">
      <CDetailPageHeader
        :breadcrumbs-items="breadcrumbsItems"
        :title="product.attributes.title"
      />

      <div
        v-if="isMobile"
        class="product-view-header-price"
      >
        {{ product.attributes.price }}
        <span class="product-view-header-price__currency">₽</span>
      </div>
    </div>

    <ProductMedia
      v-if="product.attributes.medias.length"
      :product="product"
    />

    <div class="product-view-properties">
      <ProductCharacteristics
        :product="product"
        class="product-view-properties__characteristics"
      />

      <div class="product-view-properties__price">
        {{ product.attributes.price }}
        <span class="product-view-properties__price-currency">₽</span>
      </div>
    </div>

    <div class="product-view__info">
      <CMarkedText
        v-if="product"
        class="product-view-description product-view-info-block markdown-body"
        :text="product.attributes.description"
      />

      <CMarkedText
        v-if="product && product.attributes.description_text?.length"
        class="product-view-description product-view-info-block markdown-body"
        :text="product.attributes.description_text"
      />
    </div>
  </WrapperWithCard>

  <WrapperWithCard
    :enable-card="!isMobile"
    card-classname="product-view__machines"
  >
    <CMachinesList
      :vending-machines="visibleVendingMachines ?? []"
      :is-loading="visibleVendingMachines === null"
      :titles="{ link: 'Показать автомат:', name: 'Адрес ближайшего автомата:' }"
      :is-show-more-button-rounded="isMobile"
      :empty-text="`Автоматы с данным товаром не найдены`"
      :is-location-loading="isLocationLoading"
      :is-filtering-by-location-available="
        !!userLocation && vendingMachinesForProduct?.length !== 0
      "
      :is-filtering-by-location-enabled="filterByLocationCheckbox"
      :is-filtering-by-location-loading="isFilteringByLocationLoading"
      @update:filter-by-location-checkbox="filterByLocationCheckbox = $event"
    >
      <template #link="{ machine }">
        <div>
          <div>
            Остаток
            {{
              (machine.attributes as VendingMachineWithProduct['attributes']).products.find(
                ({ product }) => product.data.id === product.data.id
              )?.in_stock
            }}
            шт.
          </div>
          <div>Показать автомат</div>
        </div>
      </template>
    </CMachinesList>
  </WrapperWithCard>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import {
    getVendingMachinesForProduct,
    VendingMachineWithProduct
  } from '@/api/requests/VendingMachines';
  import CMachinesList from '@/components/MachinesList/CMachinesList.vue';
  import ProductCharacteristics from '@/components/product/ProductCharacteristics.vue';
  import ProductMedia from '@/components/product/ProductMedia.vue';
  import { getProduct } from '@/services/product/api';
  import { Product } from '@/services/product/types';
  import CDetailPageHeader from '@/components/CDetailPageHeader/CDetailPageHeader.vue';
  import CMarkedText from '@/components/CMarkedText/CMarkedText.vue';
  import CPreloader from '@/components/CPreloader/CPreloader.vue';
  import WrapperWithCard from '@/components/Cards/WrapperWithCard.vue';
  import { useClientSize } from '@/composables/useClientSize';
  import { useVendingMachinesList } from '@/composables/useVendingMachinesList';
  import { useSectionTracking } from '@/composables/useSectionTracking';

  const route = useRoute();
  const { isMobile } = useClientSize();
  const {
    allVendingMachines,
    filterByLocationCheckbox,
    isLocationLoading,
    userLocation,
    isFilteringByLocationLoading,
    createSortMachinesComputed,
    waitForVendingMachines
  } = useVendingMachinesList();

  const { getLinkPath } = useSectionTracking();

  const product = ref<Product | null>(null);
  const vendingMachinesForProduct = ref<VendingMachineWithProduct[] | null>(null);

  const breadcrumbsItems = computed(() => {
    const first = { title: 'Главная', link: '/?to=catalog' };
    const last = { title: product.value?.attributes.title ?? '' };

    const sourceItem = getSourceBreadcrumbItem();

    if (sourceItem) {
      return [first, sourceItem, last];
    }

    return [first, last];
  });

  const { sortedMachines, initUserLocation } = createSortMachinesComputed();

  const visibleVendingMachines = computed(
    () =>
      vendingMachinesForProduct.value
        ?.filter(machine =>
          sortedMachines.value?.find(sortedMachine => sortedMachine.id === machine.id)
        )
        .sort((a, b) => {
          const aIndex = sortedMachines.value?.findIndex(
            sortedMachine => sortedMachine.id === a.id
          );
          const bIndex = sortedMachines.value?.findIndex(
            sortedMachine => sortedMachine.id === b.id
          );

          return (aIndex ?? 0) - (bIndex ?? 0);
        }) ?? null
  );

  function getSourceBreadcrumbItem() {
    if (
      route.query.from === 'main-page' &&
      route.query['from-value'] &&
      product.value?.attributes.category.data
    ) {
      return {
        title: product.value.attributes.category.data.attributes.name ?? '',
        link: getLinkPath('catalog', {
          category: route.query['from-value'].toString()
        })
      };
    }

    if (route.query.from === 'machine') {
      if (!route.query['from-value']) {
        return null;
      }

      const machine = allVendingMachines.value?.find(
        allVendingMachinesItem =>
          allVendingMachinesItem.id === parseInt(route.query['from-value']?.toString() ?? '', 10)
      );

      if (!machine) {
        return null;
      }

      return {
        title: `Автомат ${machine.attributes.erp_id}`,
        link: `/machine/${machine.id}`
      };
    }

    return null;
  }

  onMounted(async () => {
    const [productResponse, vendingMachinesResponse] = await Promise.all([
      getProduct(route.params.id as string),
      getVendingMachinesForProduct(route.params.id as string),
      initUserLocation(),
      waitForVendingMachines()
    ]);

    vendingMachinesForProduct.value = vendingMachinesResponse.data.data;

    product.value = productResponse.data;
  });
</script>

<style>
  .product-view {
    padding: 24px 0 48px;

    &__info {
      display: flex;
      padding: 0 37px;
      gap: 60px;
      align-items: flex-start;
      margin-top: 40px;
    }

    &__header {
      padding: 0 40px;
    }

    &__machines {
      margin-top: 24px;
    }

    &-header-price {
      font-size: 32px;
      font-weight: 700;
      color: #000;
      margin-bottom: 18px;
    }
  }

  .product-view-info-block {
    width: 50%;
  }

  .product-view-properties {
    display: flex;
    flex-direction: row;
    gap: 18px;
    padding: 0 40px;
    margin-top: 24px;

    &__characteristics {
      flex-grow: 1;
    }

    &__price {
      width: 297px;
      height: 175px;
      background: #86ca2f;
      border-radius: 18px;
      color: #fff;
      font-size: 54px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid #e5e5e5;
      box-sizing: border-box;
      word-wrap: normal;
      overflow: hidden;
      max-width: 100%;
      word-break: break-all;
      padding: 0 20px;
      flex-shrink: 0;
    }
  }

  @media (--mobile) {
    .product-view {
      padding: 24px 0;

      &__header {
        padding: 0 0;
      }

      &-properties {
        flex-direction: column;
        padding: 0;

        &__characteristics {
          padding: 0 0;
        }

        &__price {
          display: none;
        }
      }

      &__info {
        padding: 0 0;
        flex-direction: column;
        gap: 24px;
        margin-bottom: 24px;
      }

      &-info-block {
        width: 100%;
      }
    }

    /* .product-media-wrapper { */
    /* padding: 0 0; */
    /* } */
  }
</style>
